<template>
  <div class="agent-chat" :class="{ expanded: isExpanded, sidebar: isSidebar }">
    <!-- Chat Toggle Button (Only in non-sidebar mode) -->
    <button v-if="!isExpanded && !isSidebar" @click="toggleChat" class="chat-toggle">
      <i class="fas fa-robot"></i>
      <span class="toggle-label">Regulatory Agent</span>
      <span v-if="unreadCount > 0" class="unread-badge">{{ unreadCount }}</span>
    </button>

    <!-- Chat Panel -->
    <div v-if="isExpanded || isSidebar" class="chat-panel">
      <div class="chat-header">
        <div class="header-title">
          <i class="fas fa-robot"></i>
          <span>Regulatory Agent</span>
        </div>
        <div class="header-actions">
          <button @click="toggleMemoryPanel" class="btn-icon" :class="{ active: showMemoryPanel }" title="Agent Memory">
            <i class="fas fa-brain"></i>
          </button>
          <button @click="clearChat" class="btn-icon" title="Clear chat">
            <i class="fas fa-trash-alt"></i>
          </button>
          <button v-if="!isSidebar" @click="toggleChat" class="btn-icon" title="Minimize">
            <i class="fas fa-minus"></i>
          </button>
        </div>
      </div>
      
      <!-- Agent Memory Panel -->
      <div v-if="showMemoryPanel" class="memory-panel">
        <div class="memory-header">
          <h5><i class="fas fa-brain"></i> Agent Learning</h5>
          <button @click="refreshMemories" class="btn-icon-sm" title="Refresh">
            <i class="fas fa-sync" :class="{ 'fa-spin': loadingMemories }"></i>
          </button>
        </div>
        <div v-if="loadingMemories" class="memory-loading">
          <i class="fas fa-spinner fa-spin"></i> Loading memories...
        </div>
        <div v-else-if="memories.length === 0" class="memory-empty">
          <i class="fas fa-lightbulb"></i>
          <p>No memories yet. The agent learns from your feedback!</p>
        </div>
        <div v-else class="memory-list">
          <div v-for="mem in memories" :key="mem.id" class="memory-item" :class="mem.type">
            <div class="memory-icon">
              <i :class="getMemoryIcon(mem.type)"></i>
            </div>
            <div class="memory-content">
              <div class="memory-text">{{ mem.content }}</div>
              <div class="memory-meta">
                <span class="memory-type">{{ mem.type }}</span>
                <span class="memory-date">{{ formatMemoryDate(mem.created_at) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="chat-messages" ref="messagesContainer">
        <!-- Welcome Message -->
        <div v-if="messages.length === 0" class="welcome-message">
          <div class="welcome-icon">
            <i class="fas fa-robot"></i>
          </div>
          <h4>Regulatory Intelligence Agent</h4>
          <p>
            {{ suggestedQuestions?.length ? 'Based on your subscriptions, try asking:' : 'I can help you navigate healthcare regulatory changes. Try asking:' }}
          </p>
          <div class="suggestions">
            <button
              v-for="(q, i) in (suggestedQuestions || defaultSuggestions)"
              :key="i"
              @click="sendSuggestion(q)"
              class="suggestion-btn"
            >
              {{ truncateSuggestion(q) }}
            </button>
          </div>
        </div>

        <!-- Messages -->
        <div 
          v-for="(message, index) in messages" 
          :key="index" 
          class="message"
          :class="message.role"
        >
          <div class="message-avatar">
            <i :class="message.role === 'user' ? 'fas fa-user' : 'fas fa-robot'"></i>
          </div>
          <div class="message-content">
            <div v-if="message.proactiveInsight" class="proactive-insight">
              <i class="fas fa-lightbulb"></i>
              {{ message.proactiveInsight }}
            </div>
            <div class="message-text" v-html="formatMessage(message.content)"></div>
            
            <!-- Policy Events if present -->
            <div v-if="message.events?.length" class="events-preview">
              <div class="events-preview-header">
                <span>{{ message.events.length }} policy events</span>
                <button @click="viewAllEvents(message.events)" class="view-all-btn">
                  View All
                </button>
              </div>
              <div class="events-preview-list">
                <div v-for="event in message.events.slice(0, 5)" :key="event.id" class="preview-event">
                  <span class="event-state" v-if="event.state">{{ event.state }}</span>
                  <span class="event-title">{{ truncateText(event.title, 60) }}</span>
                  <span class="event-severity" :class="event.severity?.toLowerCase()">{{ event.severity }}</span>
                </div>
                <div v-if="message.events.length > 5" class="more-events">
                  +{{ message.events.length - 5 }} more
                </div>
              </div>
            </div>

            <!-- Action Buttons if present -->
            <div v-if="message.actions?.length" class="message-actions">
              <button 
                v-for="(action, actionIndex) in message.actions" 
                :key="actionIndex"
                @click="executeAction(action)"
                class="action-btn"
                :class="action.type"
                :disabled="action.executed"
              >
                <i :class="action.icon || 'fas fa-play'"></i>
                {{ action.label }}
                <span v-if="action.executed" class="executed-badge">Done</span>
              </button>
            </div>

            <!-- Suggested follow-up questions -->
            <div v-if="message.suggestedFollowUps?.length" class="suggested-followups">
              <span class="followup-label">Try asking:</span>
              <button
                v-for="(q, i) in message.suggestedFollowUps.slice(0, 3)"
                :key="i"
                @click="sendSuggestion(q)"
                class="followup-btn"
              >
                {{ truncateSuggestion(q) }}
              </button>
            </div>

            <div class="message-time">{{ formatTime(message.timestamp) }}</div>
            
            <!-- Feedback Buttons for Assistant Messages -->
            <div v-if="message.role === 'assistant' && message.hasAnalysis" class="message-feedback">
              <button 
                v-if="!message.feedbackGiven"
                @click="submitFeedback(message, 'approval')" 
                class="feedback-btn approve"
                title="This response is helpful"
              >
                <i class="fas fa-thumbs-up"></i>
                Helpful
              </button>
              <button 
                v-if="!message.feedbackGiven"
                @click="openCorrectionModal(message)" 
                class="feedback-btn correct"
                title="Provide correction"
              >
                <i class="fas fa-edit"></i>
                Correct
              </button>
              <span v-if="message.feedbackGiven" class="feedback-thanks">
                <i class="fas fa-check-circle"></i>
                Thanks for your feedback!
              </span>
            </div>
          </div>
        </div>

        <!-- Typing Indicator -->
        <div v-if="isTyping" class="message assistant typing">
          <div class="message-avatar">
            <i class="fas fa-robot"></i>
          </div>
          <div class="message-content">
            <div class="typing-indicator">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        </div>
      </div>

      <!-- Input Area -->
      <div class="chat-input">
        <textarea 
          v-model="inputMessage"
          @keydown.enter.prevent="sendMessage"
          placeholder="Ask about regulatory changes, policy impacts..."
          rows="1"
          ref="inputField"
        ></textarea>
        <button @click="sendMessage" :disabled="!inputMessage.trim() || isTyping" class="send-btn">
          <i class="fas fa-paper-plane"></i>
        </button>
      </div>
    </div>
    
    <!-- Correction Modal -->
    <div v-if="showCorrectionModal" class="modal-overlay" @click.self="closeCorrectionModal">
      <div class="modal-content correction-modal">
        <div class="modal-header">
          <h4><i class="fas fa-edit"></i> Provide Correction</h4>
          <button @click="closeCorrectionModal" class="btn-icon">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <p class="correction-intro">Help improve the agent by correcting its analysis.</p>
          
          <div class="form-group">
            <label>Original Response</label>
            <div class="original-analysis">{{ correctionData.original }}</div>
          </div>
          
          <div class="form-group">
            <label for="corrected">What should the correct answer be?</label>
            <textarea 
              id="corrected"
              v-model="correctionData.corrected"
              class="form-control"
              rows="3"
              placeholder="e.g., The rule actually affects MCOs, not just providers..."
            ></textarea>
          </div>
          
          <div class="form-group">
            <label for="reason">Why is this the correct information? (optional)</label>
            <textarea 
              id="reason"
              v-model="correctionData.reason"
              class="form-control"
              rows="2"
              placeholder="e.g., I verified this in the Federal Register..."
            ></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeCorrectionModal" class="btn btn-secondary">Cancel</button>
          <button 
            @click="submitCorrection" 
            class="btn btn-primary"
            :disabled="!correctionData.corrected || submittingFeedback"
          >
            <i v-if="submittingFeedback" class="fas fa-spinner fa-spin"></i>
            <i v-else class="fas fa-check"></i>
            Submit Correction
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, nextTick, watch, onMounted } from 'vue'
import api from '@/services/api'
import { sanitizeLlmResponse } from '@/utils/sanitize'

export default {
  name: 'RegulatoryAgentChat',
  
  props: {
    summary: {
      type: Object,
      default: () => ({})
    },
    isSidebar: {
      type: Boolean,
      default: false
    },
    filters: {
      type: Object,
      default: () => ({})
    }
  },
  
  emits: ['filter-events', 'show-event-detail', 'show-events-list'],
  
  setup(props, { emit }) {
    const isExpanded = ref(false)
    const messages = ref([])
    const inputMessage = ref('')
    const isTyping = ref(false)
    const unreadCount = ref(0)
    const messagesContainer = ref(null)
    const inputField = ref(null)
    const sessionId = ref(null)
    
    // Memory and feedback state
    const showMemoryPanel = ref(false)
    const memories = ref([])
    const loadingMemories = ref(false)
    const showCorrectionModal = ref(false)
    const submittingFeedback = ref(false)
    const suggestedQuestions = ref(null)
    const defaultSuggestions = [
      'What are the latest Medicaid policy changes?',
      'Show me high-impact rules affecting MCOs',
      'What 1115 waivers have been approved recently?',
      'Explain the impact of recent CMS regulations on providers'
    ]
    const correctionData = ref({
      messageIndex: null,
      original: '',
      corrected: '',
      reason: '',
      memoryId: null
    })
    
    const fetchSuggestedQuestions = async () => {
      try {
        const response = await api.get('/regulatory/agent/suggestions/')
        if (response.data?.suggested_questions?.length) {
          suggestedQuestions.value = response.data.suggested_questions
        }
      } catch {
        // Fallback to default suggestions on error
      }
    }

    const truncateSuggestion = (text) => {
      if (!text || text.length <= 45) return text
      return text.substring(0, 42) + '...'
    }

    // Initialize session ID and fetch personalized suggestions
    onMounted(() => {
      sessionId.value = localStorage.getItem('regulatory_agent_session') || generateSessionId()
      localStorage.setItem('regulatory_agent_session', sessionId.value)
      fetchSuggestedQuestions()
      if (props.isSidebar) {
        isExpanded.value = true
        nextTick(() => {
          scrollToBottom()
        })
      }
    })
    
    const generateSessionId = () => {
      return 'ras_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9)
    }
    
    const toggleChat = () => {
      if (props.isSidebar) return
      isExpanded.value = !isExpanded.value
      if (isExpanded.value) {
        unreadCount.value = 0
        nextTick(() => {
          inputField.value?.focus()
        })
      }
    }
    
    const clearChat = () => {
      messages.value = []
      // Generate new session ID on clear
      sessionId.value = generateSessionId()
      localStorage.setItem('regulatory_agent_session', sessionId.value)
    }
    
    const scrollToBottom = () => {
      nextTick(() => {
        if (messagesContainer.value) {
          messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
        }
      })
    }
    
    const formatMessage = (content) => {
      return sanitizeLlmResponse(content)
    }
    
    const formatTime = (timestamp) => {
      if (!timestamp) return ''
      const date = new Date(timestamp)
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }
    
    const truncateText = (text, maxLength) => {
      if (!text) return ''
      if (text.length <= maxLength) return text
      return text.substring(0, maxLength) + '...'
    }
    
    const sendSuggestion = (text) => {
      inputMessage.value = text
      sendMessage()
    }
    
    const sendMessage = async () => {
      const text = inputMessage.value.trim()
      if (!text || isTyping.value) return
      
      // Add user message
      messages.value.push({
        role: 'user',
        content: text,
        timestamp: new Date().toISOString()
      })
      
      inputMessage.value = ''
      scrollToBottom()
      
      // Show typing indicator
      isTyping.value = true
      
      try {
        // Call the regulatory agent API
        const response = await api.post('/regulatory/agent/chat/', {
          message: text,
          session_id: sessionId.value,
          context: {
            summary: props.summary,
            filters: props.filters,
            history: messages.value.slice(-10).map(m => ({
              role: m.role,
              content: m.content
            }))
          }
        })
        
        // Add assistant response
        const hasAnalysisContent = !!(
          response.data.events?.length > 0 ||
          response.data.analysis_type ||
          response.data.states_mentioned?.length > 0
        )
        
        const assistantMessage = {
          role: 'assistant',
          content: response.data.message,
          timestamp: new Date().toISOString(),
          actions: response.data.actions || [],
          events: response.data.events || [],
          hasAnalysis: hasAnalysisContent,
          memoryId: response.data.memory_id || null,
          feedbackGiven: false,
          proactiveInsight: response.data.proactive_insight || null,
          suggestedFollowUps: response.data.suggested_questions || null
        }
        
        messages.value.push(assistantMessage)
        
        // Update session ID if returned
        if (response.data.session_id) {
          sessionId.value = response.data.session_id
          localStorage.setItem('regulatory_agent_session', sessionId.value)
        }
        
        // If events were returned, emit to parent for display
        if (response.data.events?.length > 0) {
          emit('show-events-list', {
            title: 'Agent Results',
            subtitle: `Found ${response.data.events.length} policy events`,
            events: response.data.events
          })
        }
        
        // Increment unread if chat is minimized
        if (!isExpanded.value) {
          unreadCount.value++
        }
        
      } catch (error) {
        console.error('Error sending message:', error)
        messages.value.push({
          role: 'assistant',
          content: 'Sorry, I encountered an error processing your request. Please try again.',
          timestamp: new Date().toISOString()
        })
      } finally {
        isTyping.value = false
        scrollToBottom()
      }
    }
    
    const executeAction = async (action) => {
      if (action.executed) return
      
      if (action.type === 'filter') {
        // Apply filter to dashboard
        emit('filter-events', action.filter)
        action.executed = true
        
      } else if (action.type === 'view_event') {
        // Show event detail
        emit('show-event-detail', action.event)
        action.executed = true
        
      } else if (action.type === 'export') {
        // Export events
        try {
          const params = new URLSearchParams()
          if (action.filters) {
            Object.entries(action.filters).forEach(([key, value]) => {
              params.append(key, value)
            })
          }
          
          const response = await api.get(`/regulatory/events/export/?${params.toString()}`, {
            responseType: 'blob'
          })
          
          const blob = new Blob([response.data], { type: 'text/csv' })
          const url = window.URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = url
          a.download = `regulatory_events_${new Date().toISOString().split('T')[0]}.csv`
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)
          window.URL.revokeObjectURL(url)
          
          action.executed = true
        } catch (error) {
          console.error('Export error:', error)
          messages.value.push({
            role: 'assistant',
            content: `❌ Failed to export: ${error.message}`,
            timestamp: new Date().toISOString()
          })
          scrollToBottom()
        }
      }
    }
    
    const viewAllEvents = (events) => {
      emit('show-events-list', {
        title: 'Policy Events',
        subtitle: `${events.length} events`,
        events: events
      })
    }
    
    // =======================================================================
    // MEMORY & FEEDBACK FUNCTIONS
    // =======================================================================
    
    const toggleMemoryPanel = () => {
      showMemoryPanel.value = !showMemoryPanel.value
      if (showMemoryPanel.value && memories.value.length === 0) {
        refreshMemories()
      }
    }
    
    const refreshMemories = async () => {
      loadingMemories.value = true
      try {
        const response = await api.get('/regulatory/agent/memory/', {
          params: { limit: 10 }
        })
        memories.value = response.data.memories || []
      } catch (error) {
        console.error('Error fetching memories:', error)
        memories.value = []
      } finally {
        loadingMemories.value = false
      }
    }
    
    const getMemoryIcon = (memType) => {
      const icons = {
        episodic: 'fas fa-history',
        semantic: 'fas fa-book',
        procedural: 'fas fa-cogs',
        belief: 'fas fa-lightbulb'
      }
      return icons[memType] || 'fas fa-brain'
    }
    
    const formatMemoryDate = (dateStr) => {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      const now = new Date()
      const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24))
      
      if (diffDays === 0) return 'Today'
      if (diffDays === 1) return 'Yesterday'
      if (diffDays < 7) return `${diffDays} days ago`
      return date.toLocaleDateString()
    }
    
    const openCorrectionModal = (message) => {
      const analysisText = message.content.substring(0, 200) + (message.content.length > 200 ? '...' : '')
      correctionData.value = {
        messageIndex: messages.value.indexOf(message),
        original: analysisText,
        corrected: '',
        reason: '',
        memoryId: message.memoryId || null
      }
      showCorrectionModal.value = true
    }
    
    const closeCorrectionModal = () => {
      showCorrectionModal.value = false
      correctionData.value = {
        messageIndex: null,
        original: '',
        corrected: '',
        reason: '',
        memoryId: null
      }
    }
    
    const submitFeedback = async (message, feedbackType) => {
      submittingFeedback.value = true
      try {
        await api.post('/regulatory/agent/feedback/', {
          memory_id: message.memoryId,
          feedback_type: feedbackType,
          session_id: sessionId.value,
          original_content: message.content.substring(0, 500),
          comment: feedbackType === 'approval' ? 'User confirmed response is helpful' : ''
        })
        
        message.feedbackGiven = true
        
        if (showMemoryPanel.value) {
          refreshMemories()
        }
        
      } catch (error) {
        console.error('Error submitting feedback:', error)
        messages.value.push({
          role: 'assistant',
          content: '⚠️ Could not record feedback. Please try again.',
          timestamp: new Date().toISOString()
        })
        scrollToBottom()
      } finally {
        submittingFeedback.value = false
      }
    }
    
    const submitCorrection = async () => {
      submittingFeedback.value = true
      try {
        await api.post('/regulatory/agent/feedback/', {
          memory_id: correctionData.value.memoryId,
          feedback_type: 'correction',
          session_id: sessionId.value,
          original_content: correctionData.value.original,
          corrected_content: correctionData.value.corrected,
          reason: correctionData.value.reason
        })
        
        if (correctionData.value.messageIndex !== null) {
          messages.value[correctionData.value.messageIndex].feedbackGiven = true
        }
        
        messages.value.push({
          role: 'assistant',
          content: '✅ Thanks for the correction! I\'ve learned from your feedback.',
          timestamp: new Date().toISOString()
        })
        scrollToBottom()
        
        if (showMemoryPanel.value) {
          refreshMemories()
        }
        
        closeCorrectionModal()
        
      } catch (error) {
        console.error('Error submitting correction:', error)
        messages.value.push({
          role: 'assistant',
          content: '⚠️ Could not record correction. Please try again.',
          timestamp: new Date().toISOString()
        })
        scrollToBottom()
      } finally {
        submittingFeedback.value = false
      }
    }
    
    // Auto-scroll when messages change
    watch(messages, scrollToBottom, { deep: true })
    
    return {
      isExpanded,
      messages,
      inputMessage,
      isTyping,
      unreadCount,
      messagesContainer,
      inputField,
      sessionId,
      toggleChat,
      clearChat,
      formatMessage,
      formatTime,
      truncateText,
      truncateSuggestion,
      suggestedQuestions,
      defaultSuggestions,
      sendSuggestion,
      sendMessage,
      executeAction,
      viewAllEvents,
      // Memory & Feedback
      showMemoryPanel,
      memories,
      loadingMemories,
      showCorrectionModal,
      submittingFeedback,
      correctionData,
      toggleMemoryPanel,
      refreshMemories,
      getMemoryIcon,
      formatMemoryDate,
      openCorrectionModal,
      closeCorrectionModal,
      submitFeedback,
      submitCorrection
    }
  }
}
</script>

<style scoped>
.agent-chat {
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  z-index: 1000;
}

.chat-toggle {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  background: linear-gradient(135deg, #0d9488, #14b8a6);
  color: white;
  border: none;
  border-radius: 50px;
  cursor: pointer;
  box-shadow: 0 4px 20px rgba(13, 148, 136, 0.4);
  transition: all 0.3s;
  font-weight: 600;
}

.chat-toggle:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 25px rgba(13, 148, 136, 0.5);
}

.chat-toggle i {
  font-size: 1.25rem;
}

.unread-badge {
  background: #ef4444;
  color: white;
  font-size: 0.7rem;
  padding: 0.125rem 0.375rem;
  border-radius: 10px;
  margin-left: 0.25rem;
}

.chat-panel {
  width: 420px;
  height: 600px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 10px 50px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.chat-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.25rem;
  background: linear-gradient(135deg, #0d9488, #14b8a6);
  color: white;
}

.header-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
}

.header-title i {
  font-size: 1.25rem;
}

.header-actions {
  display: flex;
  gap: 0.5rem;
}

.btn-icon {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
}

.btn-icon:hover {
  background: rgba(255, 255, 255, 0.3);
}

.btn-icon.active {
  background: rgba(255, 255, 255, 0.4);
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.welcome-message {
  text-align: center;
  padding: 2rem 1rem;
}

.welcome-icon {
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #f0fdfa, #ccfbf1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
}

.welcome-icon i {
  font-size: 1.5rem;
  color: #0d9488;
}

.welcome-message h4 {
  margin: 0 0 0.5rem 0;
  color: #1e293b;
}

.welcome-message p {
  color: #64748b;
  margin: 0 0 1rem 0;
  font-size: 0.9rem;
}

.suggestions {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.suggestion-btn {
  padding: 0.5rem 1rem;
  background: #f0fdfa;
  border: 1px solid #99f6e4;
  border-radius: 8px;
  color: #0f766e;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.85rem;
}

.suggestion-btn:hover {
  background: #ccfbf1;
  border-color: #5eead4;
}

.message {
  display: flex;
  gap: 0.75rem;
  max-width: 90%;
}

.message.user {
  align-self: flex-end;
  flex-direction: row-reverse;
}

.message.assistant {
  align-self: flex-start;
}

.message-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.message.user .message-avatar {
  background: #3b82f6;
  color: white;
}

.message.assistant .message-avatar {
  background: linear-gradient(135deg, #0d9488, #14b8a6);
  color: white;
}

.message-content {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.proactive-insight {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: #fef3c7;
  border: 1px solid #fcd34d;
  border-radius: 8px;
  font-size: 0.85rem;
  color: #92400e;
}

.proactive-insight i {
  flex-shrink: 0;
  margin-top: 0.125rem;
}

.suggested-followups {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.375rem;
  margin-top: 0.25rem;
}

.followup-label {
  font-size: 0.75rem;
  color: #64748b;
  margin-right: 0.25rem;
}

.followup-btn {
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  color: #475569;
  cursor: pointer;
  transition: background 0.15s;
}

.followup-btn:hover {
  background: #e2e8f0;
  color: #1e293b;
}

.message-text {
  padding: 0.75rem 1rem;
  border-radius: 12px;
  line-height: 1.5;
  font-size: 0.9rem;
}

.message.user .message-text {
  background: #3b82f6;
  color: white;
  border-bottom-right-radius: 4px;
}

.message.assistant .message-text {
  background: #f1f5f9;
  color: #1e293b;
  border-bottom-left-radius: 4px;
}

.message-time {
  font-size: 0.7rem;
  color: #94a3b8;
}

.message.user .message-time {
  text-align: right;
}

/* Events Preview */
.events-preview {
  background: #f8fafc;
  border-radius: 8px;
  padding: 0.75rem;
  font-size: 0.85rem;
}

.events-preview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
  color: #64748b;
}

.view-all-btn {
  background: none;
  border: none;
  color: #0d9488;
  cursor: pointer;
  font-size: 0.8rem;
  text-decoration: underline;
}

.preview-event {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0;
  border-bottom: 1px solid #e2e8f0;
}

.preview-event:last-child {
  border-bottom: none;
}

.event-state {
  background: #e0f2fe;
  color: #0369a1;
  font-size: 0.7rem;
  padding: 0.125rem 0.375rem;
  border-radius: 4px;
  font-weight: 600;
  flex-shrink: 0;
}

.event-title {
  flex: 1;
  color: #1e293b;
  font-size: 0.8rem;
}

.event-severity {
  font-size: 0.65rem;
  padding: 0.125rem 0.375rem;
  border-radius: 4px;
  font-weight: 600;
  text-transform: uppercase;
}

.event-severity.high {
  background: #fee2e2;
  color: #991b1b;
}

.event-severity.medium {
  background: #fef3c7;
  color: #92400e;
}

.event-severity.low {
  background: #dcfce7;
  color: #166534;
}

.more-events {
  text-align: center;
  color: #94a3b8;
  padding-top: 0.5rem;
  font-size: 0.8rem;
}

/* Action Buttons */
.message-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 0.75rem;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 500;
  transition: all 0.2s;
}

.action-btn.filter {
  background: #f0fdfa;
  color: #0f766e;
}

.action-btn.filter:hover:not(:disabled) {
  background: #ccfbf1;
}

.action-btn.export {
  background: #dbeafe;
  color: #1e40af;
}

.action-btn.export:hover:not(:disabled) {
  background: #bfdbfe;
}

.action-btn.view_event {
  background: #ede9fe;
  color: #5b21b6;
}

.action-btn.view_event:hover:not(:disabled) {
  background: #ddd6fe;
}

.action-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.executed-badge {
  background: #10b981;
  color: white;
  font-size: 0.65rem;
  padding: 0.125rem 0.375rem;
  border-radius: 4px;
  margin-left: 0.25rem;
}

/* Typing Indicator */
.typing-indicator {
  display: flex;
  gap: 4px;
  padding: 0.75rem 1rem;
  background: #f1f5f9;
  border-radius: 12px;
  width: fit-content;
}

.typing-indicator span {
  width: 8px;
  height: 8px;
  background: #94a3b8;
  border-radius: 50%;
  animation: typing 1.4s infinite ease-in-out;
}

.typing-indicator span:nth-child(2) {
  animation-delay: 0.2s;
}

.typing-indicator span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes typing {
  0%, 60%, 100% {
    transform: translateY(0);
  }
  30% {
    transform: translateY(-4px);
  }
}

/* Chat Input */
.chat-input {
  display: flex;
  gap: 0.5rem;
  padding: 1rem;
  border-top: 1px solid #e2e8f0;
}

.chat-input textarea {
  flex: 1;
  padding: 0.75rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  resize: none;
  font-family: inherit;
  font-size: 0.9rem;
  outline: none;
  transition: border-color 0.2s;
}

.chat-input textarea:focus {
  border-color: #0d9488;
}

.send-btn {
  width: 44px;
  height: 44px;
  background: linear-gradient(135deg, #0d9488, #14b8a6);
  color: white;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.send-btn:hover:not(:disabled) {
  transform: scale(1.05);
}

.send-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Sidebar Mode */
.agent-chat.sidebar {
  position: static;
  width: 100%;
  height: 100%;
}

.agent-chat.sidebar .chat-panel {
  width: 100%;
  height: 100%;
  box-shadow: none;
  border: 1px solid #e2e8f0;
}

.agent-chat.sidebar .chat-toggle {
  display: none;
}

/* Memory Panel */
.memory-panel {
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  max-height: 200px;
  overflow-y: auto;
}

.memory-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  background: #f0fdfa;
  border-bottom: 1px solid #99f6e4;
  position: sticky;
  top: 0;
}

.memory-header h5 {
  margin: 0;
  font-size: 0.85rem;
  color: #0f766e;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.memory-header h5 i {
  color: #0d9488;
}

.btn-icon-sm {
  background: transparent;
  border: none;
  color: #0f766e;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
}

.btn-icon-sm:hover {
  background: #ccfbf1;
}

.memory-loading,
.memory-empty {
  padding: 1.5rem;
  text-align: center;
  color: #64748b;
  font-size: 0.85rem;
}

.memory-empty i {
  font-size: 1.5rem;
  color: #cbd5e1;
  margin-bottom: 0.5rem;
  display: block;
}

.memory-empty p {
  margin: 0;
}

.memory-list {
  padding: 0.5rem;
}

.memory-item {
  display: flex;
  gap: 0.75rem;
  padding: 0.75rem;
  background: white;
  border-radius: 8px;
  margin-bottom: 0.5rem;
  border: 1px solid #e2e8f0;
}

.memory-item:last-child {
  margin-bottom: 0;
}

.memory-icon {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 0.75rem;
}

.memory-item.procedural .memory-icon {
  background: #fef3c7;
  color: #92400e;
}

.memory-item.episodic .memory-icon {
  background: #dbeafe;
  color: #1e40af;
}

.memory-item.semantic .memory-icon {
  background: #dcfce7;
  color: #166534;
}

.memory-item.belief .memory-icon {
  background: #ede9fe;
  color: #5b21b6;
}

.memory-content {
  flex: 1;
  min-width: 0;
}

.memory-text {
  font-size: 0.8rem;
  color: #1e293b;
  line-height: 1.4;
  word-break: break-word;
}

.memory-meta {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.25rem;
  font-size: 0.7rem;
}

.memory-type {
  background: #f0fdfa;
  color: #0f766e;
  padding: 0.125rem 0.375rem;
  border-radius: 4px;
  text-transform: capitalize;
}

.memory-date {
  color: #94a3b8;
}

/* Feedback Buttons */
.message-feedback {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.feedback-btn {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.375rem 0.625rem;
  border: 1px solid;
  border-radius: 6px;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.2s;
}

.feedback-btn.approve {
  background: #f0fdf4;
  border-color: #86efac;
  color: #166534;
}

.feedback-btn.approve:hover {
  background: #dcfce7;
}

.feedback-btn.correct {
  background: #fefce8;
  border-color: #fde047;
  color: #854d0e;
}

.feedback-btn.correct:hover {
  background: #fef9c3;
}

.feedback-thanks {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.75rem;
  color: #10b981;
}

.feedback-thanks i {
  color: #10b981;
}

/* Correction Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
}

.modal-content {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.correction-modal {
  max-width: 500px;
  width: 90%;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.25rem;
  background: linear-gradient(135deg, #0d9488, #14b8a6);
  color: white;
}

.modal-header h4 {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
}

.modal-body {
  padding: 1.25rem;
}

.correction-intro {
  color: #64748b;
  font-size: 0.9rem;
  margin: 0 0 1rem 0;
}

.original-analysis {
  background: #fef3c7;
  border: 1px solid #fde68a;
  border-radius: 8px;
  padding: 0.75rem;
  font-size: 0.9rem;
  color: #92400e;
}

.form-group {
  margin-bottom: 1rem;
}

.form-group:last-child {
  margin-bottom: 0;
}

.form-group label {
  display: block;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #1e293b;
  font-size: 0.85rem;
}

.form-control {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-family: inherit;
  font-size: 0.9rem;
  resize: vertical;
}

.form-control:focus {
  outline: none;
  border-color: #0d9488;
  box-shadow: 0 0 0 3px rgba(13, 148, 136, 0.1);
}

.modal-footer {
  padding: 1rem 1.25rem;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
}

.btn {
  padding: 0.625rem 1.25rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  border: none;
}

.btn-primary {
  background: #0d9488;
  color: white;
}

.btn-primary:hover {
  background: #0f766e;
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-secondary {
  background: #f1f5f9;
  color: #475569;
}

.btn-secondary:hover {
  background: #e2e8f0;
}

/* Responsive */
@media (max-width: 480px) {
  .chat-panel {
    width: calc(100vw - 2rem);
    height: calc(100vh - 4rem);
    position: fixed;
    bottom: 1rem;
    right: 1rem;
  }
  
  .toggle-label {
    display: none;
  }
}
</style>
